/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { colors } from "@farmlands/mui-theme";
import { Box, styled, Theme, useMediaQuery } from "@mui/material";
import dayjs from "dayjs";
import React, { type JSX, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";

import Autocomplete from "../../../../components/Autocomplete";
import Checkbox from "../../../../components/Checkbox";
import { DatePicker } from "../../../../components/DateRangePicker";
import Input from "../../../../components/Input";
import MaterialSymbol from "../../../../components/MaterialSymbol";
import { PadActionButton, PadLineItem } from "../../../../components/Pad/Pad";
import TextField from "../../../../components/TextField";
import Typography from "../../../../components/Typography";
import { AddressLine, FullAddress, fullAddress } from "../../../../services/als";
import { splitAddress } from "../../../../services/als/splitAddress";
import TestIds from "../../../../testIds";
import { isValidDate, isWeekend } from "../../../../util/helpers";
import useAccountAddress from "../../../../util/useAccountAddress";
import { AccountAddress, selectCurrentAccountNumber } from "../../../azure-b2c/storage";
import { extractZipCode } from "../utils/extractZipCode";

const BottomContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "1.5rem",
  flexWrap: "wrap",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
  },
}));

const DairySupplyContainer = styled(Box)(({ theme }) => ({
  flex: "1 1",
  minWidth: 255,
  [theme.breakpoints.down("sm")]: {
    minWidth: "unset",
    display: "inline-flex",
    width: "100%",
    flex: "unset",
  },
}));

const UrgentContainer = styled(Box)(({ theme }) => ({
  flex: "1 1",
  display: "flex",
  [theme.breakpoints.up("sm")]: {
    minWidth: "322px",
  },
}));

export interface UseAddressSelectReturnType {
  addressError: string | undefined;
  addressName: string | undefined;
  addressShort: string | undefined;
  dairyNumber: string | undefined;
  dairyNumberEnabled: boolean;
  date: Date | undefined;
  dateError: string | null | undefined;
  deliveryInstructions: string;
  editAddress: boolean;
  resetAddress: () => void;
  saveAddress: boolean;
  searchResults: AddressLine[];
  searchValue: string | undefined;
  urgent: boolean | undefined;
  zipCode: string | undefined;
  selectedAddressLine: AddressLine | undefined;
  selectedFullAddress: FullAddress | undefined;
  selectedKnownAddress: AccountAddress | undefined;
  setSelectedAddressLine: React.Dispatch<React.SetStateAction<AddressLine | undefined>>;
  setSelectedFullAddress: React.Dispatch<React.SetStateAction<FullAddress | undefined>>;
  setAddressShort: React.Dispatch<React.SetStateAction<string | undefined>>;
  setAddressName: React.Dispatch<React.SetStateAction<string | undefined>>;
  setAddressError: React.Dispatch<React.SetStateAction<string | undefined>>;
  setZipCode: React.Dispatch<React.SetStateAction<string | undefined>>;
  setDairyNumber: React.Dispatch<React.SetStateAction<string | undefined>>;
  setDate: React.Dispatch<React.SetStateAction<Date | undefined>>;
  setDateError: React.Dispatch<React.SetStateAction<string | null | undefined>>;
  setDeliveryInstructions: React.Dispatch<React.SetStateAction<string>>;
  setEditAddress: React.Dispatch<React.SetStateAction<boolean>>;
  setSearchValue: React.Dispatch<React.SetStateAction<string | undefined>>;
  setSelectedKnownAddress: React.Dispatch<React.SetStateAction<AccountAddress | undefined>>;
  setSaveAddress: React.Dispatch<React.SetStateAction<boolean>>;
  AddressSelectComponent: JSX.Element;
}

export interface AddressSelectOptions {
  dateDisabled?: boolean;
  urgent?: boolean;
  urgentEnabled?: boolean;
  deliveryInstructions?: string;
  deliveryInstructionsDisabled?: boolean;
  addressNameIsRequired?: boolean;
  isNewAddress?: boolean;
  date?: Date;
  dairyNumber?: string;
  dairyNumberEnabled?: boolean;
  saveAddress?: boolean;
  saveAddressEnabled?: boolean;
  defaultToSearch?: boolean;
  enableAddAddress?: boolean;
}

export const useAddressSelect = (
  knownAddresses: AccountAddress[],
  defaultAddress?: AccountAddress,
  options?: AddressSelectOptions
): UseAddressSelectReturnType => {
  const [searchValue, setSearchValue] = React.useState<string | undefined>("");
  const [editAddress, setEditAddress] = React.useState(!!options?.defaultToSearch);
  const [addressError, setAddressError] = React.useState<string | undefined>();

  const [addressShort, setAddressShort] = React.useState(defaultAddress?.fullAddress);
  const [addressName, setAddressName] = React.useState(defaultAddress?.name);
  const [selectedKnownAddress, setSelectedKnownAddress] = React.useState<
    AccountAddress | undefined
  >(defaultAddress);
  const [zipCode, setZipCode] = React.useState<string | undefined>(defaultAddress?.zipCode);
  const [saveAddress, setSaveAddress] = React.useState(options?.saveAddress ?? false);

  const [selectedAddressLine, setSelectedAddressLine] = React.useState<AddressLine | undefined>();
  const [selectedFullAddress, setSelectedFullAddress] = React.useState<FullAddress | undefined>();

  const [timer, setTimer] = React.useState<NodeJS.Timeout>();

  const accountNumber = useSelector(selectCurrentAccountNumber);

  const [deliveryInstructions, setDeliveryInstructions] = React.useState(
    options?.deliveryInstructions ?? ""
  );
  const [date, setDate] = React.useState<Date | undefined>(options?.date);
  const [dateError, setDateError] = React.useState<string | null>();
  const [dairyNumber, setDairyNumber] = React.useState<string | undefined>(
    selectedKnownAddress?.dairySupplyNum ?? options?.dairyNumber
  );

  const [urgent, setUrgent] = React.useState(options?.urgent);

  const reset = () => {
    setDate(options?.date);
    setDateError(null);
    setDairyNumber(selectedKnownAddress?.dairySupplyNum ?? options?.dairyNumber);
    setUrgent(options?.urgent);
    setDeliveryInstructions("");

    setSearchValue(""); // Reset search value to an empty string
    setEditAddress(!!options?.defaultToSearch); // Reset edit address based on defaultToSearch option
    setAddressError(undefined); // Reset address error to undefined

    setAddressShort(defaultAddress?.fullAddress); // Reset address short to default full address
    setAddressName(defaultAddress?.name); // Reset address name to default name
    setSelectedKnownAddress(defaultAddress); // Reset selected known address to default address
    setZipCode(defaultAddress?.zipCode); // Reset zip code to default zip code
    setSaveAddress(options?.saveAddress ?? false); // Reset save address based on options

    setSelectedAddressLine(undefined); // Reset selected address line to undefined
    setSelectedFullAddress(undefined); // Reset selected full address to undefined

    if (timer) {
      clearTimeout(timer); // Clear any existing timers
      setTimer(undefined); // Reset timer to undefined
    }
  };

  useEffect(() => {
    reset();
  }, [accountNumber]);

  const { matchingKnownAddresses, searchAddressQuery } = useAccountAddress({
    searchText: searchValue,
  });

  const searchResults = React.useMemo(() => {
    if (searchAddressQuery.isSuccess) {
      return searchAddressQuery.data;
    }
    return [];
  }, [searchAddressQuery]);

  const resetAddress = useCallback(() => {
    setAddressShort(undefined);
    setSelectedAddressLine(undefined);
    setSelectedFullAddress(undefined);
    setSelectedKnownAddress(undefined);
  }, [setAddressShort, setSelectedAddressLine, setSelectedFullAddress, setSelectedKnownAddress]);

  useEffect(() => {
    if (urgent !== options?.urgent) {
      setUrgent(options?.urgent);
    }
  }, [options?.urgent]);

  return {
    addressError,
    addressName,
    addressShort,
    dairyNumber,
    dairyNumberEnabled: options?.dairyNumberEnabled ?? true,
    date,
    dateError,
    deliveryInstructions,
    editAddress,
    resetAddress,
    saveAddress,
    searchResults,
    searchValue,
    urgent,
    zipCode,
    selectedAddressLine,
    selectedFullAddress,
    selectedKnownAddress,
    setSelectedAddressLine,
    setSelectedFullAddress,
    setAddressShort,
    setAddressName,
    setAddressError,
    setZipCode,
    setDairyNumber,
    setDate,
    setDateError,
    setDeliveryInstructions,
    setEditAddress,
    setSearchValue,
    setSelectedKnownAddress,
    setSaveAddress,

    AddressSelectComponent: (
      <AddressSelect
        addressError={addressError}
        addressName={addressName}
        addressShort={addressShort}
        dairyNumber={dairyNumber}
        dairyNumberEnabled={options?.dairyNumberEnabled ?? true}
        date={date}
        dateError={dateError}
        deliveryInstructions={deliveryInstructions}
        editAddress={editAddress}
        knownAddresses={matchingKnownAddresses}
        saveAddress={saveAddress}
        searchResults={searchResults}
        searchValue={searchValue}
        urgent={urgent}
        zipCode={zipCode}
        setSelectedAddressLine={setSelectedAddressLine}
        setSelectedFullAddress={setSelectedFullAddress}
        setAddressShort={setAddressShort}
        setAddressName={setAddressName}
        setZipCode={setZipCode}
        setDairyNumber={setDairyNumber}
        setDate={setDate}
        setDateError={setDateError}
        setDeliveryInstructions={setDeliveryInstructions}
        setEditAddress={setEditAddress}
        setSearchValue={setSearchValue}
        setSelectedKnownAddress={setSelectedKnownAddress}
        setSaveAddress={setSaveAddress}
        setUrgent={setUrgent}
        options={options}
      />
    ),
  };
};

interface AddressSelectProps {
  addressError: string | undefined;
  addressName: string | undefined;
  addressShort: string | undefined;
  dairyNumber: string | undefined;
  dairyNumberEnabled: boolean;
  date: Date | undefined;
  dateError: string | null | undefined;
  deliveryInstructions: string;
  editAddress: boolean;
  knownAddresses: AccountAddress[];
  saveAddress: boolean;
  searchResults: AddressLine[];
  searchValue: string | undefined;
  urgent: boolean | undefined;
  zipCode: string | undefined;
  setSelectedAddressLine: React.Dispatch<React.SetStateAction<AddressLine | undefined>>;
  setSelectedFullAddress: React.Dispatch<React.SetStateAction<FullAddress | undefined>>;
  setUrgent: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  setAddressShort: React.Dispatch<React.SetStateAction<string | undefined>>;
  setAddressName: React.Dispatch<React.SetStateAction<string | undefined>>;
  setZipCode: React.Dispatch<React.SetStateAction<string | undefined>>;
  setDairyNumber: React.Dispatch<React.SetStateAction<string | undefined>>;
  setDate: React.Dispatch<React.SetStateAction<Date | undefined>>;
  setDateError: React.Dispatch<React.SetStateAction<string | null | undefined>>;
  setDeliveryInstructions: React.Dispatch<React.SetStateAction<string>>;
  setEditAddress: React.Dispatch<React.SetStateAction<boolean>>;
  setSearchValue: React.Dispatch<React.SetStateAction<string | undefined>>;
  setSelectedKnownAddress: React.Dispatch<React.SetStateAction<AccountAddress | undefined>>;
  setSaveAddress: React.Dispatch<React.SetStateAction<boolean>>;

  options?: {
    dateDisabled?: boolean;
    urgent?: boolean;
    urgentEnabled?: boolean;
    deliveryInstructions?: string;
    deliveryInstructionsDisabled?: boolean;
    addressNameIsRequired?: boolean;
    isNewAddress?: boolean;
    date?: Date;
    dairyNumber?: string;
    dairyNumberEnabled?: boolean;
    saveAddress?: boolean;
    saveAddressEnabled?: boolean;
    enableAddAddress?: boolean;
  };
}

export interface SelectAddressProps {
  onClickEdit: (b: boolean) => void;
  isEditable: boolean;
  addressShort: string | undefined;
  addressName: string | undefined;
  postCode: string | undefined;
  customPadding?: string;
  enableAddAddress?: boolean;
}
export const SelectedAddressButton = ({
  onClickEdit,
  isEditable,
  addressShort,
  addressName,
  postCode,
  customPadding,
  enableAddAddress = true,
}: SelectAddressProps) => {
  const selectedAddressButton = React.useMemo(() => {
    if (addressShort) {
      const { second, first } = splitAddress(addressShort);
      return (
        <PadActionButton
          customPadding={customPadding}
          onClick={() => {
            onClickEdit(true);
          }}
          buttonTestId={TestIds.feat.drawdown.v1.forms.selectedAddressButton.shortAddressButton}
          title="Deliver to:"
          subtitle={
            <Typography variant="body2" component="span">
              <Typography variant="body2" component="p" fontWeight={700}>
                {addressName}
              </Typography>
              {first}
              <br />
              {second}
            </Typography>
          }
          isEditEnabled={isEditable}
        />
      );
    }

    return (
      <PadActionButton
        onClick={() => {
          onClickEdit(true);
        }}
        buttonTestId={TestIds.feat.drawdown.v1.forms.selectedAddressButton.addAddressButton}
        title="Deliver to:"
        {...(enableAddAddress
          ? {
              isEditEnabled: true,
              actionTitle: "Add address",
              actionIcon: "add",
            }
          : {})}
      />
    );
  }, [addressShort, addressName, postCode, isEditable]);

  return selectedAddressButton;
};

export const AddressSelect: React.FC<AddressSelectProps> = ({
  addressError,
  addressName,
  addressShort,
  dairyNumber,
  dairyNumberEnabled,
  date,
  dateError,
  deliveryInstructions,
  editAddress,
  knownAddresses,
  saveAddress,
  searchResults,
  searchValue,
  urgent,
  zipCode,
  setSelectedAddressLine,
  setSelectedFullAddress,
  setUrgent,
  setAddressShort,
  setAddressName,
  setZipCode,
  setDairyNumber,
  setDate,
  setDateError,
  setDeliveryInstructions,
  setEditAddress,
  setSearchValue,
  setSelectedKnownAddress,
  setSaveAddress,
  options,
}) => {
  const [isNewAddress, setIsNewAddress] = React.useState(options?.isNewAddress ?? false);
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  useEffect(() => {
    setIsNewAddress(options?.isNewAddress ?? false);
  }, [options?.isNewAddress]);

  const supplyNumbers = React.useMemo(() => {
    const nullOrBlank = (s: string | null) => {
      return s === null || s === "";
    };

    return [
      ...new Set([
        ...knownAddresses
          .filter((a) => !nullOrBlank(a.dairySupplyNum))
          .map((e) => e.dairySupplyNum),
      ]),
    ].sort((a, b) => a?.localeCompare(b));
  }, [knownAddresses]);

  const selectedAddressButton = React.useMemo(() => {
    return (
      <SelectedAddressButton
        onClickEdit={setEditAddress}
        isEditable={!editAddress}
        addressName={addressName}
        addressShort={addressShort}
        postCode={zipCode}
        enableAddAddress={options?.enableAddAddress}
      />
    );
  }, [addressShort, addressName, zipCode, editAddress, options]);

  const renderMatchingKnownAddresses = React.useMemo(() => {
    return (
      <Box>
        {knownAddresses.map((e) => {
          return (
            <PadLineItem
              option={{
                title: (
                  <Typography variant="body2" component="span" color={"primary700"}>
                    {e.name && (
                      <Typography
                        variant="body2"
                        component={"span"}
                        color={"primary700"}
                        fontWeight={700}
                      >
                        {e.name}
                        {": "}
                      </Typography>
                    )}
                    <br />
                    {e.street}
                  </Typography>
                ),
                subtitle: `${e.city}, ${e.zipCode}`,
              }}
              data-testid={TestIds.components.address.knownAddressItem}
              data-id={TestIds.components.address.knownAddressDataId(e.name)}
              key={`${e.fullAddress}-${e.name}-${e.dairySupplyNum}`}
              onClick={async () => {
                let dairySupplyNum = dairyNumber;
                if (e.dairySupplyNum && e.dairySupplyNum !== "") {
                  dairySupplyNum = e.dairySupplyNum;
                }
                setZipCode(e.zipCode ? e.zipCode : extractZipCode(e.fullAddress));
                setAddressShort(e.fullAddress);
                setAddressName(e.name);
                setDairyNumber(dairySupplyNum);
                setSelectedKnownAddress(e);
                setSelectedAddressLine(undefined);
                setSelectedFullAddress(undefined);
                setEditAddress(false);
                setIsNewAddress(false);
              }}
            />
          );
        })}
      </Box>
    );
  }, [searchValue, knownAddresses, dairyNumber]);

  if (editAddress) {
    return (
      <Box>
        {selectedAddressButton}
        <Box sx={{ my: 3 }}>
          <Input
            label="Search address:"
            disableError
            setValue={setSearchValue}
            value={searchValue}
            testId={TestIds.components.address.searchInput}
            type="text"
            placeholder="Search address"
            outlineInputProps={{
              endAdornment: (
                <MaterialSymbol sx={{ color: colors.primary500 }}>search</MaterialSymbol>
              ),
              autoFocus: !isSm,
            }}
          />
        </Box>

        {/* Render addresses associated with account, that match search criteria */}
        {renderMatchingKnownAddresses}

        {/* Render addresses provided by address lookup api */}
        {options?.enableAddAddress !== false &&
          searchResults.map((e) => {
            const { first, second } = splitAddress(e.FullAddress);
            return (
              <PadLineItem
                option={{
                  title: first,
                  subtitle: second,
                }}
                data-testid={TestIds.components.address.searchAddressItem}
                data-id={TestIds.components.address.searchAddressDataId(e.UniqueId.toString())}
                key={e.FullAddress}
                onClick={() => {
                  if (e.UniqueId) {
                    fullAddress(e.UniqueId).then((value) => {
                      setZipCode(value[0].Postcode);
                      setSelectedFullAddress(value[0]);
                      setAddressShort(e.FullAddress);
                      setSelectedAddressLine(e);
                      setAddressName(undefined);
                      setDairyNumber(undefined);
                      setSelectedKnownAddress(undefined);
                      setEditAddress(false);
                      setIsNewAddress(true);
                    });
                  }
                }}
              />
            );
          })}
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={options?.addressNameIsRequired ? "0.7rem" : "1.5rem"}
    >
      {selectedAddressButton}
      {addressError && (
        <Typography variant="body2" color="error700">
          {addressError}
        </Typography>
      )}
      {options?.saveAddressEnabled && isNewAddress && (
        <Checkbox
          label="I want this address to be stored for future use."
          checked={saveAddress}
          onChange={(_, checked) => setSaveAddress(checked)}
          data-testid={TestIds.components.address.saveAddress}
          sx={{ marginTop: -2 }}
        />
      )}

      {options?.addressNameIsRequired && isNewAddress && (
        <Box flex={"1 1"} minWidth={255}>
          <Input
            type="text"
            value={addressName}
            setValue={setAddressName}
            label="Address Name:"
            disableError
            required
            testId={TestIds.components.address.addressName}
            helperText="Name this address for future use."
            helperTextColor={colors.grey700}
          />
        </Box>
      )}
      {!options?.deliveryInstructionsDisabled && (
        <TextField
          variant="outlined"
          label="Delivery instructions:"
          testId={TestIds.components.address.instructionsField}
          onChange={(e) => setDeliveryInstructions(e.target.value)}
          value={deliveryInstructions}
          data-testid="delivery-input"
          helperText="Please add instructions for delivery."
          helperTextColor={colors.grey700}
          inputProps={{ maxLength: 200 }}
          multiline
          maxRows={10}
          minRows={options?.addressNameIsRequired ? 2 : 3}
        />
      )}

      <BottomContainer>
        {!options?.dateDisabled && (
          <Box flex={"1 1"}>
            <DatePicker
              error={!!dateError}
              value={date ? dayjs(date) : undefined}
              setDate={(newValue) => {
                let value = newValue;
                if (value === null || isValidDate(value?.toDate())) {
                  value = value?.startOf("day") ?? undefined;
                  setDate(value?.toDate());
                }
              }}
              onAccept={() => {
                setDateError(undefined);
              }}
              onError={(error, newValue) => {
                const value = newValue;
                if (value === null || isValidDate(value?.toDate())) {
                  switch (error) {
                    case "maxDate":
                      setDateError("Date cannot be more than 1 months from today.");
                      break;
                    case "minDate":
                      setDateError("Date cannot be less than 3 working days from today.");
                      break;
                    case "invalidDate":
                      setDateError("Please enter a valid date");
                      break;
                    case "shouldDisableDate":
                      setDateError("Deliveries are restricted to weekdays");
                      break;
                    default:
                      setDateError(undefined);
                  }
                }
              }}
              minDate={dayjs().startOf("day").add(3, "days")}
              maxDate={dayjs().startOf("day").add(1, "months")}
              label="Preferred delivery date:"
              shouldDisableDate={isWeekend}
              testId={TestIds.components.address.datePickerInput}
              required
            />
            {!dateError && (
              <Typography variant="body2" color="grey700" mt={0.5}>
                Minimum delivery time 3-4 working days.
              </Typography>
            )}
            {dateError && (
              <Typography
                variant="body2"
                color="error700"
                mt={0.5}
                data-testid={TestIds.components.address.deliveryDateErrorMessage}
              >
                {dateError}
              </Typography>
            )}
          </Box>
        )}
        {dairyNumberEnabled && (
          <DairySupplyContainer>
            {supplyNumbers.length > 0 ? (
              <Autocomplete
                slotProps={{
                  popper: {
                    sx: {
                      zIndex: 9999,
                    },
                  },
                }}
                freeSolo
                testId={TestIds.components.address.dairySupplyInput}
                label={`Dairy Supply Number:`}
                options={supplyNumbers}
                value={dairyNumber}
                getOptionLabel={(option) => {
                  return option;
                }}
                onInputChange={(_, value) => {
                  setDairyNumber(value);
                }}
                textFieldProps={{
                  helperText: "Include your Dairy Supply Number, if you have one.",
                  helperTextColor: colors.grey700,
                }}
              />
            ) : (
              <TextField
                variant="outlined"
                maxRows={1}
                multiline
                value={dairyNumber}
                onChange={(event) => setDairyNumber(event.target.value)}
                label="Dairy Supply Number:"
                inputProps={{ maxLength: 60 }}
                testId={TestIds.components.address.dairySupplyInput}
                helperText="Include your Dairy Supply Number, if you have one."
                helperTextColor={colors.grey700}
              />
            )}
          </DairySupplyContainer>
        )}

        {options?.urgentEnabled && (
          <UrgentContainer>
            <Box
              alignSelf="center"
              marginLeft="auto"
              sx={{
                "@media (max-width: 744px)": {
                  marginLeft: 0,
                },
              }}
            >
              <Checkbox
                checked={urgent}
                label={
                  <Typography variant="body2" fontWeight={700} sx={{ paddingLeft: "12px" }}>
                    I want this delivered urgently
                    <br />
                    <Typography variant="body2" component="span">
                      Additional delivery fees may apply
                    </Typography>
                  </Typography>
                }
                onChange={(_, checked) => setUrgent(checked)}
                data-testid={TestIds.components.address.urgentDelivery}
              />
            </Box>
          </UrgentContainer>
        )}
      </BottomContainer>
    </Box>
  );
};
